import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { StickyEdgeShadow } from '../sticky-edge-shadow'
import { GTableBodyProps, GTableColumn } from '../types'

export const TableBody = <T,>(props: GTableBodyProps<T>) => {
	const {
		column,
		cell,
		isScrolling,
		pinShadow = { left: false, right: false },
		variant
	} = props
	const isDs = variant === 'ds'

	if (!column) return null

	const renderCell = (item: T, column: GTableColumn<T>) => {
		if (column.render) return column.render(item)
		if (column.path) return _.get(item, column.path)
		return <span className="text-xs text-t-default">N/A</span>
	}

	const { key, ...cellProps } = cell.getCellProps()
	return (
		<td
			key={key}
			style={{
				...(column?.width && !column.stretch
					? {
							width: column.width,
							maxWidth: column.width,
							minWidth: column.width,
							// A boundary cell hangs its edge highlight outside its own
							// box, so it cannot also clip to it.
							...(column.freezeEdge ? {} : { overflow: 'hidden' })
					  }
					: {}),
				// Pinned columns sit flush against their edge unless other frozen
				// columns are already parked there, in which case they clear them.
				...(column.freeze ? { left: column.freezeLeftOffset ?? 0 } : {}),
				...(column.freezeRight ? { right: column.freezeRightOffset ?? 0 } : {})
			}}
			className={twMerge(
				isDs
					? 'whitespace-nowrap px-6 py-4 font-grotesk text-base text-t-default border-b border-b-t-checkbox'
					: 'whitespace-nowrap px-4 py-4 text-base text-t-default text-semibold',
				column.hidden ? 'hidden' : '',
				column.mobileHide ? 'hidden md:table-cell' : '',
				column.tabletHide ? 'hidden lg:table-cell' : '',
				column.freeze || column.freezeRight ? 'sticky z-20 bg-t-smoke' : '',
				isScrolling ? 'bg-t-smoke' : ''
			)}
			{...cellProps}
		>
			{column.freezeEdge && (
				<StickyEdgeShadow
					side={column.freezeEdge}
					show={
						column.freezeEdge === 'right' ? pinShadow.left : pinShadow.right
					}
				/>
			)}
			<div
				className={twMerge(
					isDs ? 'flex w-full h-full gap-1' : 'flex w-full h-full px-2',
					column.className || 'justify-end'
				)}
			>
				{renderCell(cell.value, column)}
			</div>
		</td>
	)
}
